import type { RecordedEvent } from './types';

type Listener = (event: MessageEvent) => void;

export class ReplayEventSource {
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSED = 2;

  readonly CONNECTING = 0;
  readonly OPEN = 1;
  readonly CLOSED = 2;

  readonly url: string;
  readonly withCredentials = true;

  readyState: number = ReplayEventSource.OPEN;
  onmessage: Listener | null = null;
  onerror: ((event: Event) => void) | null = null;
  onopen: ((event: Event) => void) | null = null;

  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly timers: ReturnType<typeof setTimeout>[] = [];

  constructor(url: string, events: RecordedEvent[], speed: number) {
    this.url = url;
    const scale = speed > 0 ? speed : 0;
    events.forEach((event) => {
      const delay = scale === 0 ? 0 : Math.round(event.atMs / scale);
      this.timers.push(setTimeout(() => this.deliver(event), delay));
    });
  }

  addEventListener(type: string, listener: EventListener): void {
    const existing = this.listeners.get(type) ?? new Set<Listener>();
    existing.add(listener as unknown as Listener);
    this.listeners.set(type, existing);
  }

  removeEventListener(type: string, listener: EventListener): void {
    this.listeners.get(type)?.delete(listener as unknown as Listener);
  }

  close(): void {
    this.readyState = ReplayEventSource.CLOSED;
    this.timers.forEach(clearTimeout);
    this.timers.length = 0;
  }

  private deliver(event: RecordedEvent): void {
    if (this.readyState === ReplayEventSource.CLOSED) return;
    const message = { type: event.type, data: event.data, lastEventId: '', origin: '' } as MessageEvent;
    if (event.type === 'message') this.onmessage?.(message);
    this.listeners.get(event.type)?.forEach(listener => listener(message));
  }
}
